/**
 * ReminderQueueDialog - Dialog de auditoria de disparos de lembretes
 * 
 * Layout premium com KPI cards, filtros avançados (status, busca, datas),
 * tabela com barra lateral colorida por status e paginação server-side.
 * 
 * @version 2.0.0
 * RELEASE: 2026-03-29 v2.0.0 - Layout premium + KPIs + Filtros de data
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MessageSquare, Search, Clock, Send, AlertCircle, Ban,
  SkipForward, Loader2, RefreshCw, CalendarDays,
  Bell, Inbox, ExternalLink, CheckCheck, Check, X, Minus, Eye,
  ChevronRight, ChevronDown
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { ReminderQueueKpiCards } from '@/components/reminders/ReminderQueueKpiCards';
import { useReminderQueueItems, type ReminderQueueFilters } from '@/hooks/useReminderQueueItems';
import { useReminderQueueStats } from '@/hooks/useReminderQueueStats';
import { cn } from '@/lib/utils';
import type { ReminderRule } from '@/hooks/useReminderRules';

// ============================================================================
// TIPOS E CONSTANTES
// ============================================================================

interface ReminderQueueDialogProps {
  /** Regra de lembrete para exibir disparos */
  rule: ReminderRule | null;
  /** Controle de abertura do dialog */
  open: boolean;
  /** Callback ao mudar estado de abertura */
  onOpenChange: (open: boolean) => void;
}

/** Mapeamento de entity_type para label humanizado */
const ENTITY_TYPE_LABELS: Record<string, string> = {
  scheduling: 'Agendamento',
  cobranca: 'Cobrança',
  contrato: 'Contrato',
  crm: 'CRM',
};

/** Configuração de cada status para exibição */
const STATUS_CONFIG: Record<string, {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: React.ElementType;
  className?: string;
  barColor: string;
}> = {
  pending: {
    label: 'Pendente',
    variant: 'secondary',
    icon: Clock,
    className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    barColor: 'bg-yellow-500',
  },
  processing: {
    label: 'Processando',
    variant: 'default',
    icon: Loader2,
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    barColor: 'bg-blue-500',
  },
  sent: {
    label: 'Enviado',
    variant: 'default',
    icon: Send,
    className: 'bg-green-500/10 text-green-600 border-green-500/20',
    barColor: 'bg-green-500',
  },
  failed: {
    label: 'Falha',
    variant: 'destructive',
    icon: AlertCircle,
    className: 'bg-destructive/10 text-destructive border-destructive/20',
    barColor: 'bg-destructive',
  },
  cancelled: {
    label: 'Cancelado',
    variant: 'outline',
    icon: Ban,
    className: 'text-muted-foreground',
    barColor: 'bg-muted-foreground/40',
  },
  skipped: {
    label: 'Ignorado',
    variant: 'outline',
    icon: SkipForward,
    className: 'text-muted-foreground',
    barColor: 'bg-muted-foreground/40',
  },
};

/** Tabs de filtro por status */
const STATUS_TABS = [
  { value: null, label: 'Todos' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'sent', label: 'Enviados' },
  { value: 'failed', label: 'Falhas' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'skipped', label: 'Ignorados' },
];

/** Tipo para filtros de data */
interface DateFilters {
  createdFrom?: Date;
  createdTo?: Date;
  remindFrom?: Date;
  remindTo?: Date;
  sentFrom?: Date;
  sentTo?: Date;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function ReminderQueueDialog({
  rule,
  open,
  onOpenChange,
}: ReminderQueueDialogProps) {
  const queryClient = useQueryClient();

  // Ao abrir o modal, invalida cache para garantir dados frescos do banco
  useEffect(() => {
    if (open && rule?.id) {
      queryClient.invalidateQueries({ queryKey: ['reminder-queue-items', rule.id] });
      queryClient.invalidateQueries({ queryKey: ['reminder-queue-stats', rule.id] });
    }
  }, [open, rule?.id, queryClient]);

  // Estado de filtros e paginação
  const [filters, setFilters] = useState<ReminderQueueFilters>({
    status: null,
    search: null,
  });
  const [dateFilters, setDateFilters] = useState<DateFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Estado do modal de chat
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);

  // Estado de linhas expandidas (toggle de detalhes)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  /** Alterna expansão de uma linha */
  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Estado do popover de datas
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  /** Monta filtros combinados (status/busca + datas) */
  const combinedFilters = useMemo<ReminderQueueFilters>(() => ({
    ...filters,
    createdFrom: dateFilters.createdFrom?.toISOString() || null,
    createdTo: dateFilters.createdTo?.toISOString() || null,
    remindFrom: dateFilters.remindFrom?.toISOString() || null,
    remindTo: dateFilters.remindTo?.toISOString() || null,
    sentFrom: dateFilters.sentFrom?.toISOString() || null,
    sentTo: dateFilters.sentTo?.toISOString() || null,
  }), [filters, dateFilters]);

  // Busca dados via RPC paginada (lazy: só quando dialog está aberto)
  const {
    items,
    totalCount,
    isLoading,
    isFetching,
    refetch,
  } = useReminderQueueItems(
    rule?.id,
    combinedFilters,
    { page, pageSize },
    open && !!rule
  );

  // Busca estatísticas para KPIs (v1.1.0: sincronizado com filtros ativos)
  const { stats, isLoading: statsLoading } = useReminderQueueStats(
    rule?.id,
    open && !!rule,
    combinedFilters
  );

  /** Humaniza offset_minutes da regra */
  const offsetLabel = useMemo(() => {
    if (!rule) return '';
    const abs = Math.abs(rule.offset_minutes);
    const dir = rule.offset_minutes < 0 ? 'antes' : 'depois';
    if (abs < 60) return `${abs} min ${dir}`;
    if (abs < 1440) return `${Math.floor(abs / 60)}h ${dir}`;
    const days = Math.floor(abs / 1440);
    return `${days} dia${days > 1 ? 's' : ''} ${dir}`;
  }, [rule]);

  /** Handler de busca */
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value || null }));
    setPage(1);
  };

  /** Handler de filtro por status */
  const handleStatusFilter = (status: string | null) => {
    setFilters(prev => ({ ...prev, status }));
    setPage(1);
  };

  /** Conta filtros de data ativos */
  const activeDateFiltersCount = useMemo(() => {
    let count = 0;
    if (dateFilters.createdFrom || dateFilters.createdTo) count++;
    if (dateFilters.remindFrom || dateFilters.remindTo) count++;
    if (dateFilters.sentFrom || dateFilters.sentTo) count++;
    return count;
  }, [dateFilters]);

  /** Limpa todos os filtros de data */
  const clearDateFilters = () => {
    setDateFilters({});
    setPage(1);
  };

  /** Formata data para exibição */
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), "dd/MM/yy HH:mm", { locale: ptBR });
    } catch {
      return '—';
    }
  };

  /** Renderiza status com ícone colorido e texto preto (sem badge) */
  const renderStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    const isProcessing = status === 'processing';
    // Extrai a cor do texto da configuração para o ícone
    const textColorMatch = config.className?.match(/text-[a-z]+(-\d+)?/);
    const iconColorClass = textColorMatch ? textColorMatch[0] : '';

    return (
      <div className="flex items-center gap-1 text-[10px] font-medium text-black">
        <Icon className={cn('w-3 h-3', isProcessing && 'animate-spin', iconColorClass)} />
        {config.label}
      </div>
    );
  };

  /** Renderiza status de leitura com ícone colorido e texto preto (sem badge) */
  const renderReadStatusBadge = (status: string | null) => {
    if (!status) {
      return <Minus className="w-3.5 h-3.5 text-muted-foreground mx-auto" />;
    }

    const config: Record<string, { label: string; icon: React.ElementType; className: string }> = {
      read: { label: 'Lida', icon: CheckCheck, className: 'text-blue-600 bg-blue-500/10 border-blue-500/20' },
      delivered: { label: 'Entregue', icon: CheckCheck, className: 'text-muted-foreground bg-muted border-muted' },
      sent: { label: 'Enviada', icon: Check, className: 'text-muted-foreground bg-muted border-muted' },
      pending: { label: 'Pendente', icon: Clock, className: 'text-yellow-600 bg-yellow-500/10 border-yellow-500/20' },
      failed: { label: 'Falhou', icon: X, className: 'text-destructive bg-destructive/10 border-destructive/20' },
    };

    const c = config[status] || config.pending;
    const Icon = c.icon;
    // Extrai a cor do texto da configuração para o ícone
    const textColorMatch = c.className.match(/text-[a-z]+(-\d+)?/);
    const iconColorClass = textColorMatch ? textColorMatch[0] : '';

    return (
      <div className="flex items-center gap-1 text-[10px] font-medium text-black">
        <Icon className={cn('w-3 h-3', iconColorClass)} />
        {c.label}
      </div>
    );
  };

  /** Formata data curta para exibição no filtro */
  const formatShortDate = (date?: Date) => {
    if (!date) return '';
    return format(date, 'dd/MM/yy', { locale: ptBR });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-0 gap-0">
          {/* ============================================================= */}
          {/* HEADER */}
          {/* ============================================================= */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                  <Bell className="w-5 h-5 text-primary" />
                  Disparos da Regra
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2 flex-wrap">
                  {/* Badge de offset */}
                  <Badge variant="secondary" className="text-[10px] gap-1">
                    <Clock className="w-3 h-3" />
                    {offsetLabel}
                  </Badge>
                  {/* Badge de inbox */}
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Inbox className="w-3 h-3" />
                    {rule?.inbox_name}
                  </Badge>
                </DialogDescription>
              </div>

              {/* Botão refresh */}
              <Button
                variant="outline"
                size="sm"
                onClick={refetch}
                disabled={isFetching}
                className="gap-1.5"
              >
                <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
                Atualizar
              </Button>
            </div>
          </DialogHeader>

          {/* ============================================================= */}
          {/* KPI CARDS */}
          {/* ============================================================= */}
          <ReminderQueueKpiCards stats={stats} loading={statsLoading} />

          {/* ============================================================= */}
          {/* BARRA DE FILTROS */}
          {/* ============================================================= */}
          <div className="px-6 py-3 border-b bg-muted/30 shrink-0 flex flex-col sm:flex-row gap-3 sm:items-center">
            {/* Filtro por status via badges clicáveis */}
            <div className="flex items-center gap-1 flex-wrap">
              {STATUS_TABS.map(tab => (
                <button
                  key={tab.value ?? 'all'}
                  onClick={() => handleStatusFilter(tab.value)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                    filters.status === tab.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Botão filtros de data */}
            <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    'gap-1.5 h-8 text-xs',
                    activeDateFiltersCount > 0 && 'border-primary text-primary'
                  )}
                >
                  <CalendarDays className="w-3.5 h-3.5" />
                  Datas
                  {activeDateFiltersCount > 0 && (
                    <Badge className="h-4 w-4 p-0 flex items-center justify-center text-[9px] rounded-full">
                      {activeDateFiltersCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start" side="bottom">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Filtros de Data</h4>
                    {activeDateFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={clearDateFilters}>
                        Limpar
                      </Button>
                    )}
                  </div>

                  {/* Data Criação */}
                  <DateRangeFilter
                    label="Data Criação"
                    from={dateFilters.createdFrom}
                    to={dateFilters.createdTo}
                    onFromChange={(d) => { setDateFilters(prev => ({ ...prev, createdFrom: d })); setPage(1); }}
                    onToChange={(d) => { setDateFilters(prev => ({ ...prev, createdTo: d })); setPage(1); }}
                  />

                  {/* Data Agendamento */}
                  <DateRangeFilter
                    label="Data Agendamento"
                    from={dateFilters.remindFrom}
                    to={dateFilters.remindTo}
                    onFromChange={(d) => { setDateFilters(prev => ({ ...prev, remindFrom: d })); setPage(1); }}
                    onToChange={(d) => { setDateFilters(prev => ({ ...prev, remindTo: d })); setPage(1); }}
                  />

                  {/* Data Envio */}
                  <DateRangeFilter
                    label="Data Envio"
                    from={dateFilters.sentFrom}
                    to={dateFilters.sentTo}
                    onFromChange={(d) => { setDateFilters(prev => ({ ...prev, sentFrom: d })); setPage(1); }}
                    onToChange={(d) => { setDateFilters(prev => ({ ...prev, sentTo: d })); setPage(1); }}
                  />
                </div>
              </PopoverContent>
            </Popover>

            {/* Campo de busca */}
            <div className="relative sm:ml-auto sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar contato..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>

          {/* ============================================================= */}
          {/* TABELA DE RESULTADOS — Layout Premium */}
          {/* ============================================================= */}
          <div className="flex-1 overflow-auto min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Carregando disparos...
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
                <Bell className="w-8 h-8 opacity-30" />
                <p className="text-sm">Nenhum disparo encontrado</p>
                {(filters.status || filters.search || activeDateFiltersCount > 0) ? (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setFilters({ status: null, search: null });
                      setDateFilters({});
                      setPage(1);
                    }}
                  >
                    Limpar filtros
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="overflow-auto h-full">
                {/* Tabela com largura mínima para scroll horizontal — padrão FunnelNodeDrilldownModal */}
                <table className="min-w-[900px] w-full caption-bottom text-xs">
                  {/* Header sticky */}
                  <thead className="sticky top-0 bg-background z-[1] [&_tr]:border-b">
                    <tr className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                      <th className="w-[6px] p-0" /> {/* Barra lateral */}
                      <th className="w-[24px] p-0" /> {/* Seta expandir */}
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Status</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Tipo</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Contato</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Telefone</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Agendado</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Enviado</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Visualizado</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Criado</th>
                      <th className="text-[10px] font-medium text-center px-2 py-1.5 text-muted-foreground">Tent.</th>
                      <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Erro</th>
                      <th className="text-[10px] font-medium text-right px-2 py-1.5 text-muted-foreground w-[60px]">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                      const isExpanded = expandedRows.has(item.id);
                      return (
                        <React.Fragment key={item.id}>
                          {/* Linha principal — clicável para expandir */}
                          <tr
                            className="border-b border-border/40 hover:bg-muted/40 group transition-colors cursor-pointer"
                            onClick={() => toggleRow(item.id)}
                          >
                            {/* Barra lateral colorida por status */}
                            <td className="p-0 w-[6px]">
                              <div className={cn('w-1 h-full min-h-[40px] rounded-full', statusCfg.barColor)} />
                            </td>

                            {/* Seta expandir/recolher */}
                            <td className="p-0 w-[24px] text-center">
                              {isExpanded
                                ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground inline-block" />
                                : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground inline-block transition-transform" />
                              }
                            </td>

                            {/* Status */}
                            <td className="px-2 py-1.5">{renderStatusBadge(item.status)}</td>

                            {/* Tipo Evento */}
                            <td className="px-2 py-1.5">
                              <Badge variant="outline" className="text-[10px] font-medium">
                                {ENTITY_TYPE_LABELS[item.entity_type] || item.entity_type}
                              </Badge>
                            </td>

                            {/* Contato */}
                            <td className="px-2 py-1.5 font-medium text-xs max-w-[140px]">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate block">{item.contact_name || '—'}</span>
                                </TooltipTrigger>
                                {item.contact_name && (
                                  <TooltipContent side="top">
                                    <p className="text-xs">{item.contact_name}</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </td>

                            {/* Telefone */}
                            <td className="px-2 py-1.5 text-xs text-muted-foreground font-mono">
                              {item.contact_phone || '—'}
                            </td>

                            {/* Agendado para */}
                            <td className="px-2 py-1.5 text-xs text-muted-foreground">
                              {formatDate(item.remind_at)}
                            </td>

                            {/* Enviado em */}
                            <td className="px-2 py-1.5 text-xs text-muted-foreground">
                              {formatDate(item.sent_at)}
                            </td>

                            {/* Visualizado — status de leitura */}
                            <td className="px-2 py-1.5 text-xs">
                              {renderReadStatusBadge(item.message_read_status)}
                            </td>

                            {/* Criado em */}
                            <td className="px-2 py-1.5 text-xs text-muted-foreground">
                              {formatDate(item.created_at)}
                            </td>

                            {/* Tentativas */}
                            <td className="px-2 py-1.5 text-center text-xs text-muted-foreground">
                              {item.attempts}
                            </td>

                            {/* Erro */}
                            <td className="px-2 py-1.5 text-xs max-w-[120px]">
                              {item.error_details ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-destructive truncate block cursor-help">
                                      {item.error_details.length > 25
                                        ? `${item.error_details.substring(0, 25)}…`
                                        : item.error_details}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[300px]">
                                    <p className="text-xs whitespace-pre-wrap">{item.error_details}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>

                            {/* Ações */}
                            <td className="px-2 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                              {item.conversation_id && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => setChatConversationId(item.conversation_id)}
                                  title="Ver Chat"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </td>
                          </tr>

                          {/* Linha expandida — detalhes do item */}
                          {isExpanded && (
                            <tr className="border-b border-border/40 bg-muted/20">
                              <td colSpan={13} className="px-4 py-3">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                  {/* Mensagem */}
                                  <div className="col-span-2 md:col-span-3">
                                    <span className="text-muted-foreground font-medium block mb-0.5">
                                      {item.template_name ? 'Template' : 'Mensagem'}
                                    </span>
                                    <p className="bg-background rounded px-2 py-1.5 border border-border/40 whitespace-pre-wrap text-xs leading-relaxed max-h-[120px] overflow-auto">
                                      {item.template_name || item.message_text || '—'}
                                    </p>
                                  </div>

                                  {/* Inbox */}
                                  <div>
                                    <span className="text-muted-foreground font-medium block mb-0.5">Inbox</span>
                                    <span>{item.inbox_name || '—'}</span>
                                  </div>

                                  {/* Entity Type */}
                                  <div>
                                    <span className="text-muted-foreground font-medium block mb-0.5">Tipo Entidade</span>
                                    <span>{ENTITY_TYPE_LABELS[item.entity_type] || item.entity_type}</span>
                                  </div>

                                  {/* Entity ID (truncado) */}
                                  <div>
                                    <span className="text-muted-foreground font-medium block mb-0.5">Entity ID</span>
                                    <span className="font-mono text-[10px]">{item.entity_id?.substring(0, 8) || '—'}</span>
                                  </div>

                                  {/* Erro completo (se houver) */}
                                  {item.error_details && (
                                    <div className="col-span-2 md:col-span-3">
                                      <span className="text-destructive font-medium block mb-0.5">Erro Completo</span>
                                      <p className="bg-destructive/5 text-destructive rounded px-2 py-1.5 border border-destructive/20 whitespace-pre-wrap text-[11px] max-h-[100px] overflow-auto">
                                        {item.error_details}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ============================================================= */}
          {/* FOOTER DE PAGINAÇÃO */}
          {/* ============================================================= */}
          {totalCount > 0 && (
            <div className="px-6 pb-4 shrink-0">
              <PaginationFooter
                totalItems={totalCount}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 15, 20, 50]}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Chat — renderizado fora do DialogContent para evitar conflito z-index */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={!!chatConversationId}
        onOpenChange={(open) => { if (!open) setChatConversationId(null); }}
      />
    </>
  );
}

// ============================================================================
// COMPONENTE: FILTRO DE RANGE DE DATA
// ============================================================================

/**
 * Componente auxiliar para filtro de range de data (de/até)
 * Usado dentro do Popover de filtros de data
 */
function DateRangeFilter({
  label,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  label: string;
  from?: Date;
  to?: Date;
  onFromChange: (date?: Date) => void;
  onToChange: (date?: Date) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        {/* De */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-7 text-[11px] w-[100px] justify-start',
                !from && 'text-muted-foreground'
              )}
            >
              <CalendarDays className="w-3 h-3 mr-1" />
              {from ? format(from, 'dd/MM/yy', { locale: ptBR }) : 'De'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={from}
              onSelect={(d) => onFromChange(d || undefined)}
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        <span className="text-xs text-muted-foreground">até</span>

        {/* Até */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-7 text-[11px] w-[100px] justify-start',
                !to && 'text-muted-foreground'
              )}
            >
              <CalendarDays className="w-3 h-3 mr-1" />
              {to ? format(to, 'dd/MM/yy', { locale: ptBR }) : 'Até'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={to}
              onSelect={(d) => onToChange(d || undefined)}
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        {/* Limpar este filtro */}
        {(from || to) && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="h-6 w-6"
            onClick={() => { onFromChange(undefined); onToChange(undefined); }}
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
